/**
 * Widget público del módulo Clases (sede con `classesEnabled`): lo usan
 * hoteles y cliente final para apuntar grupos a una clase viendo las
 * plazas libres en tiempo real.
 *
 * Flujo en una pantalla: tira semanal de días con clase → tarjetas de las
 * clases del día con "Quedan X de Y" → formulario inline (personas + datos)
 * → confirmación con código. El control de cupo definitivo lo hace el
 * backend dentro de una transacción (candado anti-carrera): un 409 aquí
 * solo refresca las plazas y lo explica.
 *
 * i18n: 5 idiomas vía useT() (claves `classes*`), igual que el flujo de
 * mesas. Lo que escribe el negocio (nombre de clase, profesor, aviso) se
 * muestra tal cual, sin traducir.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Clock,
  MapPin,
  Phone,
  Users,
  GraduationCap,
  Minus,
  Plus,
} from "lucide-react";
import { format, addDays } from "date-fns";
import PoweredBy from "../components/PoweredBy";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useT } from "../i18n";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Org {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
  logo: string | null;
  announcement: string | null;
}

interface PublicSession {
  templateId: string;
  name: string;
  emoji: string | null;
  teacherName: string | null;
  startTime: string;
  durationMinutes: number;
  capacity: number;
  remaining: number;
}

interface SuccessData {
  confirmationCode: string;
  date: string;
  time: string;
  partySize: number;
  className: string;
  teacherName: string | null;
}

const mondayOf = (d: Date) => addDays(d, -((d.getDay() + 6) % 7));

export default function ClassesApp() {
  const { slug } = useParams<{ slug: string }>();
  const { t, lang, formatDate, weekdays } = useT();

  const [org, setOrg] = useState<Org | null>(null);
  const [orgError, setOrgError] = useState<string | null>(null);

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [daysMap, setDaysMap] = useState<Record<string, number>>({});
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);
  const [sessions, setSessions] = useState<PublicSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const sessionsSeq = useRef(0);

  const [openTemplate, setOpenTemplate] = useState<string | null>(null);
  const [form, setForm] = useState({
    partySize: 1,
    name: "",
    email: "",
    phone: "",
    isCompany: false,
    companyName: "",
    marketing: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessData | null>(null);

  // ——— Negocio ———
  useEffect(() => {
    if (!slug) return;
    fetch(`${API_URL}/public/organizations/${slug}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data?.message || t("classesLoadError"));
        }
        setOrg(data.organization);
      })
      .catch((err) => setOrgError(err.message ?? t("classesLoadError")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // ——— Días con clase de la semana visible (cruce de mes incluido) ———
  const weekMonths = Array.from(
    new Set([
      format(weekStart, "yyyy-MM"),
      format(addDays(weekStart, 6), "yyyy-MM"),
    ]),
  ).join(",");

  useEffect(() => {
    if (!slug) return;
    let stale = false;
    Promise.all(
      weekMonths.split(",").map((m) =>
        fetch(
          `${API_URL}/public/organizations/${slug}/classes/days?month=${m}`,
        )
          .then((res) => res.json())
          .then((data) => (data.success ? data.data?.days || [] : [])),
      ),
    )
      .then((lists) => {
        if (stale) return;
        const map: Record<string, number> = {};
        for (const list of lists) {
          for (const d of list) map[d.date] = d.sessions;
        }
        setDaysMap(map);
      })
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [slug, weekMonths]);

  // ——— Clases del día ———
  const fetchSessions = useCallback(async () => {
    if (!slug) return;
    const seq = ++sessionsSeq.current;
    setSessionsLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/public/organizations/${slug}/classes/sessions?date=${selectedDate}`,
      );
      const data = await res.json();
      if (seq !== sessionsSeq.current) return;
      setSessions(data.success ? data.data || [] : []);
    } catch {
      if (seq === sessionsSeq.current) setSessions([]);
    } finally {
      if (seq === sessionsSeq.current) setSessionsLoading(false);
    }
  }, [slug, selectedDate]);

  useEffect(() => {
    fetchSessions();
    setOpenTemplate(null);
    setSubmitError(null);
  }, [fetchSessions]);

  // ——— Alta ———
  const openBooking = (s: PublicSession) => {
    setOpenTemplate(s.templateId);
    setSubmitError(null);
    setForm((f) => ({ ...f, partySize: Math.min(f.partySize, s.remaining) }));
  };

  const handleSubmit = async (s: PublicSession) => {
    if (form.name.trim().length < 2 || !EMAIL_REGEX.test(form.email.trim())) {
      setSubmitError(t("classesFieldsError"));
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(
        `${API_URL}/public/organizations/${slug}/classes/bookings`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            date: selectedDate,
            templateId: s.templateId,
            partySize: form.partySize,
            customerName: form.name.trim(),
            customerEmail: form.email.trim(),
            customerPhone: form.phone.trim() || undefined,
            companyName:
              form.isCompany && form.companyName.trim()
                ? form.companyName.trim()
                : undefined,
            acceptsMarketing: form.marketing,
            language: lang,
          }),
        },
      );
      const data = await res.json();
      if (res.status === 409) {
        const remaining = data?.data?.remaining ?? 0;
        setSubmitError(
          remaining > 0
            ? t("classesOnlyLeft", { n: remaining })
            : t("classesFullNow"),
        );
        fetchSessions();
        return;
      }
      if (!res.ok || !data.success) {
        throw new Error(data?.message || t("commonConnectionErrorRetry"));
      }
      setSuccess(data.data);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : t("commonConnectionErrorRetry"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resetAfterSuccess = () => {
    setSuccess(null);
    setOpenTemplate(null);
    setForm({
      partySize: 1,
      name: "",
      email: "",
      phone: "",
      isCompany: false,
      companyName: "",
      marketing: false,
    });
    fetchSessions();
  };

  // ——— Render ———
  if (orgError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white border border-red-200 rounded-2xl p-8 max-w-md text-center shadow">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900">{orgError}</h1>
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (success) {
    const successDate = new Date(`${success.date}T00:00:00`);
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 max-w-md w-full text-center">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-1">
              {t("classesSuccessTitle")}
            </h1>
            <p className="text-gray-700 font-medium capitalize">
              {formatDate(successDate, "long")} · {success.time}
            </p>
            <p className="text-gray-700">
              {success.className}
              {success.teacherName
                ? ` · ${t("classesTeacher", { name: success.teacherName })}`
                : ""}
            </p>
            <p className="text-gray-600 text-sm mb-4">
              {t(
                success.partySize === 1
                  ? "commonPeopleOne"
                  : "commonPeopleOther",
                { n: success.partySize },
              )}
            </p>
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl py-3 px-4 mb-4">
              <p className="text-xs text-indigo-500 font-medium uppercase tracking-wide">
                {t("classesSuccessCode")}
              </p>
              <p className="text-2xl font-bold text-indigo-700 tracking-wider">
                {success.confirmationCode}
              </p>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              {t("classesSuccessEmailSent")}
            </p>
            <button
              onClick={resetAfterSuccess}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl"
            >
              {t("classesNewBooking")}
            </button>
          </div>
        </div>
        <PoweredBy slug={slug} etiqueta={t("commonPoweredBy")} className="pb-4" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 max-w-lg w-full mx-auto px-4 py-6">
        {/* Cabecera del negocio */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            {org.logo ? (
              <img
                src={org.logo}
                alt={org.name}
                className="w-12 h-12 rounded-xl object-cover border border-gray-200 flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-6 h-6 text-indigo-600" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="font-bold text-gray-900 text-lg leading-tight truncate">
                {org.name}
              </h1>
              <div className="text-xs text-gray-500 flex flex-wrap gap-x-3">
                {org.address && (
                  <span className="flex items-center gap-1 min-w-0">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{org.address}</span>
                  </span>
                )}
                {org.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {org.phone}
                  </span>
                )}
              </div>
            </div>
          </div>
          <LanguageSwitcher />
        </div>

        {org.announcement && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-xl px-4 py-2.5 mb-4">
            {org.announcement}
          </div>
        )}

        {/* Tira semanal */}
        <div className="bg-white rounded-2xl border border-gray-200 p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setWeekStart((d) => addDays(d, -7))}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
              title={t("classesWeekPrev")}
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <span className="text-sm font-semibold text-gray-800 capitalize">
              {t("classesPickDay")}
            </span>
            <button
              onClick={() => setWeekStart((d) => addDays(d, 7))}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
              title={t("classesWeekNext")}
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 7 }, (_, i) => {
              const day = addDays(weekStart, i);
              const dateStr = format(day, "yyyy-MM-dd");
              const isPast = dateStr < todayStr;
              const count = daysMap[dateStr] ?? 0;
              const isSelected = dateStr === selectedDate;
              const disabled = isPast || count === 0;
              return (
                <button
                  key={dateStr}
                  onClick={() => !disabled && setSelectedDate(dateStr)}
                  disabled={disabled}
                  className={`rounded-lg py-1.5 text-center border transition-colors ${
                    isSelected
                      ? "bg-indigo-600 border-indigo-600 text-white"
                      : disabled
                        ? "bg-gray-50 border-transparent text-gray-300"
                        : "bg-indigo-50 border-indigo-100 text-gray-800 hover:bg-indigo-100"
                  }`}
                >
                  <div className="text-[10px] uppercase font-medium">
                    {weekdays[i]}
                  </div>
                  <div className="text-sm font-bold tabular-nums">
                    {format(day, "d")}
                  </div>
                  <div
                    className={`text-[9px] font-medium ${
                      isSelected
                        ? "text-white/80"
                        : disabled
                          ? "text-transparent"
                          : "text-indigo-600"
                    }`}
                  >
                    {count > 0 ? count : "·"}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Clases del día */}
        <p className="text-sm font-semibold text-gray-800 capitalize mb-2">
          {formatDate(new Date(`${selectedDate}T00:00:00`), "long")}
        </p>
        {sessionsLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-7 h-7 animate-spin text-indigo-500" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-500">
            <GraduationCap className="w-9 h-9 mx-auto mb-2 text-gray-300" />
            {t("classesNoClassesThisDay")}
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((s) => {
              const full = s.remaining <= 0;
              const low = !full && s.remaining <= s.capacity * 0.25;
              const isOpen = openTemplate === s.templateId;
              return (
                <div
                  key={s.templateId}
                  className={`bg-white rounded-2xl border overflow-hidden ${
                    isOpen ? "border-indigo-300 shadow-md" : "border-gray-200"
                  }`}
                >
                  <button
                    onClick={() => !full && openBooking(s)}
                    disabled={full}
                    className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 ${
                      full ? "opacity-60" : "hover:bg-indigo-50/40"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900">
                        <span className="tabular-nums">{s.startTime}</span>
                        {" · "}
                        {s.emoji ? `${s.emoji} ` : ""}
                        {s.name}
                      </p>
                      <p className="text-xs text-gray-500 flex flex-wrap gap-x-3 mt-0.5">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {t("classesMinutes", { n: s.durationMinutes })}
                        </span>
                        {s.teacherName && (
                          <span>
                            {t("classesTeacher", { name: s.teacherName })}
                          </span>
                        )}
                      </p>
                    </div>
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                        full
                          ? "bg-red-100 text-red-700"
                          : low
                            ? "bg-amber-100 text-amber-700"
                            : "bg-green-100 text-green-700"
                      }`}
                    >
                      {full
                        ? t("classesFull")
                        : t("classesSeatsLeft", {
                            n: s.remaining,
                            total: s.capacity,
                          })}
                    </span>
                  </button>

                  {isOpen && !full && (
                    <div className="border-t border-gray-100 px-4 py-4 space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("classesPeople")}
                        </label>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                partySize: Math.max(1, f.partySize - 1),
                              }))
                            }
                            className="w-9 h-9 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                          >
                            <Minus className="w-4 h-4 text-gray-600" />
                          </button>
                          <span className="text-lg font-bold tabular-nums w-10 text-center">
                            {form.partySize}
                          </span>
                          <button
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                partySize: Math.min(
                                  s.remaining,
                                  f.partySize + 1,
                                ),
                              }))
                            }
                            className="w-9 h-9 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                          >
                            <Plus className="w-4 h-4 text-gray-600" />
                          </button>
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            {t("classesSeatsLeft", {
                              n: s.remaining,
                              total: s.capacity,
                            })}
                          </span>
                        </div>
                      </div>
                      <input
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                        placeholder={t("classesName")}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm"
                      />
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                        placeholder={t("classesEmail")}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm"
                      />
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) =>
                          setForm({ ...form, phone: e.target.value })
                        }
                        placeholder={t("classesPhone")}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm"
                      />
                      <label className="flex items-start gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={form.isCompany}
                          onChange={(e) =>
                            setForm({ ...form, isCompany: e.target.checked })
                          }
                          className="mt-0.5"
                        />
                        {t("classesCompanyToggle")}
                      </label>
                      {form.isCompany && (
                        <input
                          value={form.companyName}
                          onChange={(e) =>
                            setForm({ ...form, companyName: e.target.value })
                          }
                          placeholder={t("classesCompanyName")}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm"
                        />
                      )}
                      <label className="flex items-start gap-2 text-xs text-gray-500">
                        <input
                          type="checkbox"
                          checked={form.marketing}
                          onChange={(e) =>
                            setForm({ ...form, marketing: e.target.checked })
                          }
                          className="mt-0.5"
                        />
                        {t("classesMarketing")}
                      </label>
                      {submitError && (
                        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                          {submitError}
                        </p>
                      )}
                      <button
                        onClick={() => handleSubmit(s)}
                        disabled={submitting}
                        className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {submitting && (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        )}
                        {submitting
                          ? t("classesSubmitting")
                          : t("classesSubmit")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <PoweredBy slug={slug} etiqueta={t("commonPoweredBy")} className="pb-4" />
    </div>
  );
}
